import type { ActionFunction } from '@remix-run/node';
import { json } from '@remix-run/node';
import type {
    RemoveBuildHubMutation,
    RemoveBuildHubMutationVariables,
} from 'generated/graphql';
import { RemoveBuildHubDocument } from 'generated/graphql';
import invariant from 'tiny-invariant';

export const REMOVE_BUILD_HUB_API_ROUTE = '/remix-api/app/build-hub-remove';

const DEFAULT_FAILED_MESSAGE = 'Failed to remove BuildHub';

/**
 * API route for deleting build hubs with comprehensive error handling
 *
 * Handles validation, GraphQL errors, and provides detailed error responses
 *
 * @param request - The incoming request with form data containing build hub ID
 * @param context - Remix context with GraphQL client
 * @returns JSON response with success/error status and appropriate HTTP status codes
 */
export const action: ActionFunction = async ({ request, context }) => {
    const data = await request.formData();

    invariant(data.get('id'), 'BuildHub ID is required.');

    const variables: RemoveBuildHubMutationVariables = {
        BuildHubID: data.get('id') as string,
    };

    try {
        // Using properly typed GraphQL mutation with generated types
        const { data: result } = await context.client.mutate<
            RemoveBuildHubMutation,
            RemoveBuildHubMutationVariables
        >({
            mutation: RemoveBuildHubDocument,
            variables,
        });

        const validResult = result?.buildHubDelete;
        if (!validResult) {
            return json({
                buildHubUpsert: {
                    __typename: 'ServerError',
                    Message: DEFAULT_FAILED_MESSAGE,
                },
            });
        }

        return json(result);
    } catch (error) {
        return json({
            buildHubUpsert: {
                __typename: 'ServerError',
                Message:
                    error instanceof Error
                        ? error.message
                        : DEFAULT_FAILED_MESSAGE,
            },
        });
    }
};
